/**
 * Module preloader with error detection for stale chunks.
 * Tracks loaded chunks and provides version checking utilities.
 */

// Track successfully loaded chunks
const loadedChunks = new Set<string>();

/**
 * Preload a module with error handling.
 * Returns true if successful, false if failed (non-critical).
 */
export async function preloadModule(
  importFn: () => Promise<any>,
  chunkName: string
): Promise<boolean> {
  if (loadedChunks.has(chunkName)) return true;
  
  try {
    await importFn();
    loadedChunks.add(chunkName);
    return true;
  } catch (error) {
    console.warn(`[modulePreloader] Failed to preload ${chunkName}:`, error);
    // Don't throw - preloading is non-critical
    return false;
  }
}

/**
 * Check if the app version has changed by comparing build timestamps.
 * This detects when the server has new chunks but the browser has stale manifests.
 */
export async function checkForStaleChunks(): Promise<boolean> {
  try {
    // Fetch the build manifest with cache-busting
    const response = await fetch(`/build-manifest.json?t=${Date.now()}`, { 
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    
    if (!response.ok) {
      // No manifest available - can't check for staleness
      return false;
    }
    
    const manifest = await response.json();
    const newVersion = manifest.version;
    
    // Compare with stored version
    const storedVersion = sessionStorage.getItem('app_build_version');
    
    if (storedVersion && storedVersion !== newVersion) {
      console.info('[modulePreloader] New app version detected:', {
        current: storedVersion,
        new: newVersion
      });
      return true; // Stale chunks detected
    }
    
    // Store current version
    sessionStorage.setItem('app_build_version', newVersion);
    return false;
  } catch (error) {
    // Can't check - assume not stale
    console.debug('[modulePreloader] Version check failed:', error);
    return false;
  }
}

/**
 * Check if stale chunks were detected during lazy loading
 */
export function wasStaleChunkDetected(): boolean {
  return sessionStorage.getItem('stale_chunks_detected') === 'true';
}

/**
 * Clear the stale chunk detection flag
 */
export function clearStaleChunkFlag(): void {
  sessionStorage.removeItem('stale_chunks_detected');
}
